/**
 * El tema, que es lo único que esta interfaz configura por sí sola.
 *
 * `tokens.css` ya deja el interruptor preparado: el tema por defecto es el del
 * sistema --oscuro, o claro si el sistema lo pide-- y las clases `.light` y
 * `.dark` mandan sobre él. Aquí solo se elige cuál de las tres cosas vale y se
 * recuerda en el navegador; ningún color vive en este archivo.
 *
 * No va al backend a propósito: es una preferencia de ESTE navegador y no un
 * dato del participante. El día que haya preferencias de usuario de verdad,
 * cambia de dónde sale `readTheme` y nada más.
 */
export const THEMES = ["system", "light", "dark"] as const;

export type Theme = (typeof THEMES)[number];

export const THEME_LABELS: Record<Theme, string> = {
  system: "Sistema",
  light: "Claro",
  dark: "Oscuro",
};

const STORAGE_KEY = "embajadores:theme";

const isTheme = (value: string | null): value is Theme =>
  value !== null && (THEMES as readonly string[]).includes(value);

/**
 * Lo que se eligió la última vez, o el sistema.
 *
 * El acceso va en `try`: en un navegador con el almacenamiento bloqueado
 * `localStorage` no devuelve vacío, lanza, y un ajuste no puede tumbar la
 * aplicación entera.
 */
export const readTheme = (): Theme => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isTheme(stored) ? stored : "system";
  } catch {
    return "system";
  }
};

export const applyTheme = (theme: Theme): void => {
  const root = document.documentElement;
  root.classList.toggle("light", theme === "light");
  root.classList.toggle("dark", theme === "dark");

  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Si no se puede recordar, el tema vale para esta sesión y ya.
  }
};
